/*
 * Shopify cart integration helpers extracted from area-selector2.js.
 * Exposes functions used by CoordinateGrabberWidget to manage button cloning and cart submission.
 */

(function() {
  'use strict';

  function setupCartControls(widget, status) {
    const addToCartBtn = document.querySelector(
      '[name="add"], .btn.product-form__cart-submit, .product-form__buttons button[type="submit"], form[action*="/cart/add"] button[type="submit"]'
    );

    if (!addToCartBtn) {
      console.warn('Shopify add to cart button not found');
      return;
    }

    const form = addToCartBtn.closest('form');
    if (!form) {
      console.warn('Add to cart form not found');
      return;
    }
    const variantInput = form.querySelector('input[name="id"]');

    cleanupAddToCartButton(widget, addToCartBtn, form);

    if (status === 'confirmed') {
      addToCartBtn.style.display = 'none';
    } else {
      addToCartBtn.style.display = 'none';

      widget.originalAddToCartButton = addToCartBtn;
      widget.originalForm = form;
      widget.originalVariantId = variantInput ? variantInput.value : null;
    }
  }

  function cleanupAddToCartButton(widget, addToCartBtn, form) {
    const existingOverlay = form.querySelector('.coordinate-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }

    if (form.areaSubmitHandler) {
      form.removeEventListener('submit', form.areaSubmitHandler, true);
      delete form.areaSubmitHandler;
    }

    if (addToCartBtn.areaClickHandler) {
      addToCartBtn.removeEventListener('click', addToCartBtn.areaClickHandler, true);
      delete addToCartBtn.areaClickHandler;
    }

    addToCartBtn.style.pointerEvents = '';
    addToCartBtn.style.opacity = '';
    addToCartBtn.style.display = '';
  }

  async function addToCartWithAreaData(widget, form, options = {}) {
    if (!widget.currentSelectedArea || !widget.placeName) {
      console.error('Missing coordinates or place name');
      alert('Please complete both map selection and place name entry');
      return;
    }

    if (widget.isSubmitting) {
      return;
    }

    const {
      variantId = null,
      additionalProperties = {},
      triggerButton = null,
      loadingText = null,
      successRedirect
    } = options;

    const redirectTarget = successRedirect === undefined ? '/cart' : successRedirect;

    widget.isSubmitting = true;
    toggleFinalActionButtonsDisabled(widget, true, triggerButton || null);
    setButtonLoading(triggerButton || null, true, loadingText || 'Adding...');

    // STL generation removed - GLB-only pipeline
    // The GLB file serves as both preview and final output

    try {
      const formData = new FormData(form);

      if (variantId) {
        formData.set('id', variantId);
      }

      if (!formData.get('quantity')) {
        formData.set('quantity', '1');
      }

      const { topRight, bottomLeft, rotation } = widget.currentSelectedArea;

      formData.set('properties[_Coordinates Top-Right Lat]', topRight.lat.toFixed(6));
      formData.set('properties[_Coordinates Top-Right Lng]', topRight.lng.toFixed(6));
      formData.set('properties[_Coordinates Bottom-Left Lat]', bottomLeft.lat.toFixed(6));
      formData.set('properties[_Coordinates Bottom-Left Lng]', bottomLeft.lng.toFixed(6));

      const orientationName = getOrientationName(rotation || 0);
      formData.set('properties[Map Orientation]', `${orientationName} facing up`);
      formData.set('properties[_Rotation Degrees]', (rotation || 0).toString());

      if (widget.placeName) {
        formData.set('properties[Engraving]', widget.placeName);
      }

      let previewUrl = null;
      let normalUrl = null;

      if (widget.__landmarkFileUrls) {
        const fileUrls = widget.__landmarkFileUrls;
        // STL URL removed - GLB-only pipeline
        previewUrl = fileUrls.preview_url;
        normalUrl = fileUrls.normal_url;
      } else if (widget.currentPreviewJobId) {
        try {
          // Use validated backend URL from coordinate widget
          const backendUrl = widget.backendUrl || 'https://thelandmark-api-preview.fly.dev';
          const jobResponse = await fetch(`${backendUrl}/jobs/${widget.currentPreviewJobId}`);
          if (jobResponse.ok) {
            const jobData = await jobResponse.json();
            // STL URL removed - GLB-only pipeline
            previewUrl = jobData.assets?.preview_url || jobData.assets?.glb_url;
            normalUrl = jobData.assets?.normal_url || null;
          }
        } catch (error) {
          console.warn('Failed to fetch file URLs from preview job:', error);
        }
      }

      if (previewUrl) {
        formData.set('properties[_Preview Image URL]', previewUrl);
      }
      // STL URL removed - GLB-only pipeline
      if (normalUrl) {
        formData.set('properties[_Normal Map URL]', normalUrl);
      }

      const jobId = widget.currentPreviewJobId || widget.__landmarkJobId;
      if (jobId) {
        formData.set('properties[Job ID]', jobId);
      }

      if (additionalProperties && typeof additionalProperties === 'object') {
        Object.entries(additionalProperties).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.set(`properties[${key}]`, value);
          }
        });
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        await response.json();

        if (redirectTarget) {
          setTimeout(() => {
            window.location.href = redirectTarget;
          }, 300);
        }

      } else {
        const errorText = await response.text();
        let errorPayload = null;
        try {
          errorPayload = JSON.parse(errorText);
        } catch (parseError) {
          // Keep raw text if JSON parsing fails
        }

        const cartMessage = extractCartErrorMessage(errorPayload) || errorText || `Failed to add to cart: ${response.status} ${response.statusText}`;
        throw new Error(cartMessage);
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert(error?.message || 'Failed to add to cart. Please try again.');
    } finally {
      widget.isSubmitting = false;
      setButtonLoading(triggerButton || null, false);
      toggleFinalActionButtonsDisabled(widget, false);
    }
  }

  function extractCartErrorMessage(payload) {
    if (!payload) return null;
    if (typeof payload === 'string') return payload;

    if (payload.description) return payload.description;
    if (payload.message) return payload.message;

    if (payload.errors) {
      if (typeof payload.errors === 'string') return payload.errors;
      if (Array.isArray(payload.errors)) return payload.errors.join(', ');
      if (typeof payload.errors === 'object') {
        return Object.entries(payload.errors)
          .map(([key, value]) => {
            if (Array.isArray(value)) return `${key}: ${value.join(', ')}`;
            return `${key}: ${value}`;
          })
          .join('; ');
      }
    }

    return null;
  }

  function setButtonLoading(button, isLoading, loadingText = 'Adding...') {
    if (!button) return;

    if (isLoading) {
      if (!button.dataset.originalText) {
        button.dataset.originalText = button.textContent;
      }
      button.disabled = true;
      button.classList.add('is-loading');
      if (loadingText) {
        button.textContent = loadingText;
      }
    } else {
      button.disabled = false;
      button.classList.remove('is-loading');
      if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText;
      }
    }
  }

  function toggleFinalActionButtonsDisabled(widget, disabled, excludeButton = null) {
    const buttons = [widget.finalAddToCartButton, widget.freeMockupButton].filter(Boolean);
    buttons.forEach((btn) => {
      if (btn !== excludeButton) {
        btn.disabled = disabled;
      }
    });
  }

  function getOrientationName(rotation) {
    const names = { 0: 'North', 90: 'East', 180: 'South', 270: 'West' };
    return names[rotation] || 'North';
  }

  window.Landmark = window.Landmark || {};
  window.Landmark.ShopifyIntegration = {
    setupCartControls,
    cleanupAddToCartButton,
    addToCartWithAreaData,
    extractCartErrorMessage,
    setButtonLoading,
    toggleFinalActionButtonsDisabled,
    getOrientationName,
  };
})();
